import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  deleteUser as firebaseDeleteUser,
  User as FirebaseUser
} from "firebase/auth";
import { doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { getAuth, getDb, getFunctions } from "../firebase/instance";
import { httpsCallable } from "firebase/functions";
import { testTagFromEmail } from "../utils/testAccount";
import { signupConsentFields } from "../config/legalConsent";
import type { User, UserRole } from "../state/authStore";

/**
 * Sign up a new user with Firebase Authentication
 */
export async function signUpUser(
  email: string,
  password: string,
  name: string,
  role: UserRole
): Promise<{ success: boolean; user?: User; error?: string }> {
  try {
    // Validate inputs
    if (!email || !password || !name) {
      return { success: false, error: "All fields are required" };
    }

    if (password.length < 6) {
      return { success: false, error: "Password must be at least 6 characters" };
    }

    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(getAuth(), email, password);
    const firebaseUser = userCredential.user;

    // Create user document in Firestore
    const user: User = {
      id: firebaseUser.uid,
      email: firebaseUser.email || email,
      name,
      role,
      createdAt: new Date().toISOString(),
    };

    await setDoc(doc(getDb(), "users", firebaseUser.uid), {
      email: user.email,
      name: user.name,
      role: user.role,
      createdAt: user.createdAt,
      hasCompletedOnboarding: false,
      // Terms/privacy consent was collected by the required checkbox on the
      // signup form before this call; stamp the accepted document revision.
      ...signupConsentFields(user.createdAt),
      // Auto-tag if the email matches the prod-test alias convention
      // (e.g., rafi+prodtest1@gmail.com). Tagged docs are eligible for the
      // purgeTestData admin callable's cleanup pass.
      ...testTagFromEmail(user.email),
    });

    return { success: true, user: { ...user, hasCompletedOnboarding: false } };
  } catch (error: any) {
    // Only log detailed errors in development to reduce console noise
    if (__DEV__) {
      console.log("Sign up error code:", error?.code);
    }

    // Handle specific Firebase errors
    if (error.code === "auth/email-already-in-use") {
      return { success: false, error: "An account with this email already exists" };
    } else if (error.code === "auth/invalid-email") {
      return { success: false, error: "Invalid email address" };
    } else if (error.code === "auth/weak-password") {
      return { success: false, error: "Password is too weak" };
    }

    return { success: false, error: "Failed to create account. Please try again." };
  }
}

/**
 * Sign in an existing user with Firebase Authentication
 */
export async function signInUser(
  email: string,
  password: string
): Promise<{ success: boolean; user?: User; error?: string }> {
  try {
    // Validate inputs
    if (!email || !password) {
      return { success: false, error: "Email and password are required" };
    }

    // Sign in with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(getAuth(), email, password);
    const firebaseUser = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(getDb(), "users", firebaseUser.uid));

    if (!userDoc.exists()) {
      return { success: false, error: "User data not found" };
    }

    const userData = userDoc.data();
    const user: User = {
      id: firebaseUser.uid,
      email: firebaseUser.email || email,
      name: userData.name,
      role: userData.role,
      createdAt: userData.createdAt,
      profilePicture: userData.profilePicture || undefined,
      hasCompletedOnboarding: userData.hasCompletedOnboarding ?? true,
    };

    return { success: true, user };
  } catch (error: any) {
    // Only log detailed errors in development to reduce console noise
    if (__DEV__) {
      console.log("Sign in error code:", error?.code);
    }

    // Handle specific Firebase errors
    if (error.code === "auth/invalid-credential") {
      return {
        success: false,
        error: "Invalid email or password. If you had an account before, please sign up again to create a new account."
      };
    } else if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password") {
      return { success: false, error: "Invalid email or password" };
    } else if (error.code === "auth/invalid-email") {
      return { success: false, error: "Invalid email address" };
    } else if (error.code === "auth/too-many-requests") {
      return { success: false, error: "Too many failed attempts. Please try again later." };
    }

    return { success: false, error: "Failed to sign in. Please try again." };
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(getAuth());
  // Clear the vendor profile HERE, where "the user is leaving" is known.
  //
  // The onAuthStateChange listener cannot tell sign-out from signup — it emits
  // `callback(null)` for a signed-in user whose users/{uid} doc has not landed
  // yet — so clearing there wiped a brand-new vendor's profile mid-signup.
  // Without any clear at all, a mid-session sign-out left the previous
  // vendor's profile in the store marked "loaded", which capability gates read
  // as authoritative.
  try {
    const { useServiceProviderStore } = await import("../state/serviceProviderStore");
    await useServiceProviderStore.getState().switchUser(null, "");
  } catch (err) {
    console.warn("[auth] could not clear vendor profile on sign-out:", err);
  }
}

/**
 * Listen to authentication state changes
 */
/**
 * Map a raw Firebase Auth user into the app's `User` shape by hydrating the
 * `users/{uid}` Firestore doc. Returns `null` when there is no Firebase user
 * or the doc does not exist yet (e.g. the post-createUserWithEmailAndPassword
 * window during signup).
 *
 * This is the SINGLE source of truth for the raw→app user mapping (note the
 * app's User has `name`, not `displayName`). `onAuthStateChange` uses it, and
 * RootNavigator's cold-start hydration-race recovery reuses it so its
 * synthesized user is byte-identical — do NOT hand-roll a second mapping.
 */
export async function mapFirebaseUserToAppUser(
  firebaseUser: FirebaseUser | null
): Promise<User | null> {
  if (!firebaseUser) return null;
  // Get user data from Firestore
  const userDoc = await getDoc(doc(getDb(), "users", firebaseUser.uid));
  if (!userDoc.exists()) return null;
  const userData = userDoc.data();
  const user: User = {
    id: firebaseUser.uid,
    email: firebaseUser.email || "",
    name: userData.name || firebaseUser.displayName || "User",
    role: userData.role || "customer", // Default to customer if no role set
    createdAt: userData.createdAt || new Date().toISOString(),
    profilePicture: userData.profilePicture || undefined,
    // Legacy user docs predate this flag — treat them as completed
    // so existing accounts don't get re-shown onboarding.
    hasCompletedOnboarding: userData.hasCompletedOnboarding ?? true,
    // Load userType so returning multi-managers route to VenuePicker.
    // Without this, the multi-manager guard in RootNavigator's routing
    // (and the (vendor)/layout guard on web) silently fails and the
    // user falls through to a different branch — symptom is a stuck
    // loading spinner or being sent to account-type re-onboarding.
    userType: userData.userType,
  };
  return user;
}

export function onAuthStateChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(getAuth(), async (firebaseUser: FirebaseUser | null) => {
    callback(await mapFirebaseUserToAppUser(firebaseUser));
  });
}

/**
 * Update a customer's display name in Firestore
 */
export async function updateUserName(userId: string, name: string): Promise<void> {
  await updateDoc(doc(getDb(), "users", userId), { name });
}

/**
 * Update a customer's profile picture URL in Firestore
 */
export async function updateUserProfilePicture(userId: string, profilePicture: string): Promise<void> {
  await updateDoc(doc(getDb(), "users", userId), { profilePicture });
}

/**
 * Delete a user account and ALL associated data from Firestore
 * This function performs a comprehensive cleanup of all user-related data:
 * - User profile document
 * - Service provider profile (if applicable)
 * - All conversations (and messages within)
 * - All bookings (both as customer and vendor)
 * - All notifications
 * - All events
 * - Cart data
 * - User preferences
 * - Authentication account
 */
export async function deleteUserAccount(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`[deleteUserAccount] Starting deletion for user: ${userId}`);

    // 1. Delete user profile document
    console.log("[deleteUserAccount] Deleting user profile...");
    try {
      await deleteDoc(doc(getDb(), "users", userId));
      console.log("[deleteUserAccount] ✓ User profile deleted");
    } catch (error) {
      console.log("[deleteUserAccount] User profile not found or already deleted");
    }

    // 3. Delete all conversations where user is a participant
    console.log("[deleteUserAccount] Deleting conversations...");
    try {
      const conversationsRef = collection(getDb(), "conversations");

      // Find conversations where user is customerId
      const customerConversationsQuery = query(conversationsRef, where("customerId", "==", userId));
      const customerConversationsSnapshot = await getDocs(customerConversationsQuery);

      // Find conversations where user is vendorId
      const vendorConversationsQuery = query(conversationsRef, where("vendorId", "==", userId));
      const vendorConversationsSnapshot = await getDocs(vendorConversationsQuery);

      const allConversations = [
        ...customerConversationsSnapshot.docs,
        ...vendorConversationsSnapshot.docs
      ];

      for (const conversationDoc of allConversations) {
        // Delete all messages in the conversation
        const messagesRef = collection(getDb(), "conversations", conversationDoc.id, "messages");
        const messagesSnapshot = await getDocs(messagesRef);

        for (const messageDoc of messagesSnapshot.docs) {
          await deleteDoc(doc(getDb(), "conversations", conversationDoc.id, "messages", messageDoc.id));
        }

        // Delete the conversation document
        await deleteDoc(doc(getDb(), "conversations", conversationDoc.id));
      }

      console.log(`[deleteUserAccount] ✓ Deleted ${allConversations.length} conversations`);
    } catch (error) {
      console.log("[deleteUserAccount] Error deleting conversations:", error);
    }

    // 4. Delete all bookings (both as customer and as vendor)
    console.log("[deleteUserAccount] Deleting bookings...");
    try {
      const bookingsRef = collection(getDb(), "bookings");

      // Find bookings where user is customer
      const customerBookingsQuery = query(bookingsRef, where("customerId", "==", userId));
      const customerBookingsSnapshot = await getDocs(customerBookingsQuery);

      // Find bookings where user is vendor
      const vendorBookingsQuery = query(bookingsRef, where("vendorId", "==", userId));
      const vendorBookingsSnapshot = await getDocs(vendorBookingsQuery);

      const allBookings = [
        ...customerBookingsSnapshot.docs,
        ...vendorBookingsSnapshot.docs
      ];

      for (const bookingDoc of allBookings) {
        await deleteDoc(doc(getDb(), "bookings", bookingDoc.id));
      }

      console.log(`[deleteUserAccount] ✓ Deleted ${allBookings.length} bookings`);
    } catch (error) {
      console.log("[deleteUserAccount] Error deleting bookings:", error);
    }

    // 5. Delete all events created by user
    console.log("[deleteUserAccount] Deleting events...");
    try {
      const eventsRef = collection(getDb(), "events");
      const eventsQuery = query(eventsRef, where("customerId", "==", userId));
      const eventsSnapshot = await getDocs(eventsQuery);

      for (const eventDoc of eventsSnapshot.docs) {
        await deleteDoc(doc(getDb(), "events", eventDoc.id));
      }

      console.log(`[deleteUserAccount] ✓ Deleted ${eventsSnapshot.docs.length} events`);
    } catch (error) {
      console.log("[deleteUserAccount] Error deleting events:", error);
    }

    // 6. Delete all notifications for user
    console.log("[deleteUserAccount] Deleting notifications...");
    try {
      const notificationsRef = collection(getDb(), "notifications");
      const notificationsQuery = query(notificationsRef, where("userId", "==", userId));
      const notificationsSnapshot = await getDocs(notificationsQuery);

      for (const notificationDoc of notificationsSnapshot.docs) {
        await deleteDoc(doc(getDb(), "notifications", notificationDoc.id));
      }

      console.log(`[deleteUserAccount] ✓ Deleted ${notificationsSnapshot.docs.length} notifications`);
    } catch (error) {
      console.log("[deleteUserAccount] Error deleting notifications:", error);
    }

    // 7. Delete user subcollections (cart, preferences, events)
    console.log("[deleteUserAccount] Deleting user subcollections...");
    try {
      // Delete cart data
      try {
        await deleteDoc(doc(getDb(), "users", userId, "cart", "data"));
      } catch (error) {
        // Ignore if doesn't exist
      }

      // Delete preferences
      try {
        await deleteDoc(doc(getDb(), "users", userId, "preferences", "data"));
      } catch (error) {
        // Ignore if doesn't exist
      }

      // Delete events subcollection
      try {
        const eventsSubRef = collection(getDb(), "users", userId, "events");
        const eventsSubSnapshot = await getDocs(eventsSubRef);
        for (const eventDoc of eventsSubSnapshot.docs) {
          await deleteDoc(doc(getDb(), "users", userId, "events", eventDoc.id));
        }
      } catch (error) {
        // Ignore if doesn't exist
      }

      console.log("[deleteUserAccount] ✓ User subcollections deleted");
    } catch (error) {
      console.log("[deleteUserAccount] Error deleting subcollections:", error);
    }

    console.log(`[deleteUserAccount] ✓ Successfully deleted all data for user: ${userId}`);
    console.log("[deleteUserAccount] Note: Firebase Auth account must be deleted separately through Firebase Console");

    // 8. SERVER-SIDE, and LAST. `deleteMyAccount` deletes the owned
    //    collections, the users doc, and finally the vendor profile — then the
    //    Auth user, with the admin SDK.
    //
    //    Ordering is load-bearing in BOTH directions and has been wrong twice:
    //      - profile FIRST left a live Auth user with no profile on any partial
    //        failure → sign back in, re-create, planTier absent → free CRM.
    //      - Auth deleted INSIDE the callable but BEFORE this cascade killed the
    //        principal mid-run, so steps 3-7 executed as a deleted user and
    //        worked only while the cached token happened to still be valid.
    //
    //    Running the callable last fixes both: the client cascade completes as a
    //    live user, then the server destroys the profile and the Auth account
    //    back-to-back with admin credentials — so there is no window where a
    //    profile is missing while a sign-in survives, and no `requires-recent-
    //    login` failure can strand one (the admin SDK has no recency rule; the
    //    client-side `firebaseDeleteUser` did, with no reauthenticate anywhere
    //    in the repo).
    console.log("[deleteUserAccount] Deleting vendor data + auth server-side...");
    const deleteMyAccount = httpsCallable<{ confirm: boolean }, { ok: boolean }>(
      getFunctions(),
      "deleteMyAccount"
    );
    await deleteMyAccount({ confirm: true });
    console.log("[deleteUserAccount] ✓ Server-side deletion complete");

    return { success: true };
  } catch (error: any) {
    console.error("[deleteUserAccount] Error during user deletion:", error);
    return {
      success: false,
      error: error.message || "Failed to delete user account and associated data"
    };
  }
}

/**
 * Delete current authenticated user's account (including Auth and all Firestore data)
 * This should be called by the user themselves from within the app
 */
export async function deleteCurrentUserAccount(): Promise<{ success: boolean; error?: string }> {
  try {
    const firebaseUser = getAuth().currentUser;

    if (!firebaseUser) {
      return { success: false, error: "No user is currently signed in" };
    }

    const userId = firebaseUser.uid;

    // First delete all Firestore data
    const firestoreResult = await deleteUserAccount(userId);

    if (!firestoreResult.success) {
      return firestoreResult;
    }

    // The Auth account is deleted SERVER-SIDE by `deleteMyAccount`, which runs
    // last in the cascade above. This client call is therefore expected to
    // throw `user-not-found`, which is SUCCESS here.
    //
    // It cannot be the primary path: `firebaseDeleteUser` requires a RECENT
    // sign-in and this repo has no `reauthenticate` call anywhere, so for any
    // older session it fails deterministically — which, with the profile
    // already deleted, is precisely the live-auth-no-profile free-CRM state.
    // The admin SDK has no recency rule. Kept only for the transitional window
    // where an older callable is still deployed.
    console.log("[deleteCurrentUserAccount] Deleting Firebase Auth account...");
    try {
      await firebaseDeleteUser(firebaseUser);
      console.log("[deleteCurrentUserAccount] ✓ Firebase Auth account deleted");
    } catch (authErr: any) {
      if (authErr?.code === "auth/user-not-found" || authErr?.code === "auth/user-token-expired") {
        console.log("[deleteCurrentUserAccount] ✓ Auth account already removed server-side");
      } else {
        throw authErr;
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error("[deleteCurrentUserAccount] Error:", error);

    if (error.code === "auth/requires-recent-login") {
      return {
        success: false,
        error: "For security reasons, please sign out and sign in again before deleting your account"
      };
    }

    return {
      success: false,
      error: error.message || "Failed to delete account"
    };
  }
}
